/* Export a React Flow diagram to a downloadable PDF.
 * Captures the WHOLE graph (not just the visible viewport) at high resolution
 * via html-to-image, then writes a jsPDF page sized to the diagram's dimensions
 * so detail stays crisp for large diagrams. Shared by the IaC architecture
 * diagram and the planning/recovery visualization canvas.
 *
 * Where supported (Chromium), prompts the user for the filename + location via
 * the File System Access API; otherwise falls back to a normal download. */
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { getNodesBounds } from "@xyflow/react";

export async function exportFlowToPdf(opts: {
  nodes: any[];
  /** The `.react-flow__viewport` element of the canvas to capture. */
  viewportEl: HTMLElement | null;
  filename: string;
  pixelRatio?: number;
  pad?: number;
}): Promise<void> {
  const { nodes, viewportEl, filename, pixelRatio = 2, pad = 40 } = opts;
  if (!nodes?.length || !viewportEl) return;

  // 1) Ask the user where to save FIRST — showSaveFilePicker must run inside the
  //    click's user activation, before any long await. Cancel aborts the export.
  let fileHandle: any = null;
  const picker = (window as any).showSaveFilePicker;
  if (typeof picker === "function") {
    try {
      fileHandle = await picker({
        suggestedName: filename,
        types: [{ description: "PDF document", accept: { "application/pdf": [".pdf"] } }],
      });
    } catch (err: any) {
      if (err?.name === "AbortError") return; // user cancelled the dialog
      fileHandle = null; // unsupported/blocked → fall back to a normal download
    }
  }

  // 2) Render the diagram at natural size + high resolution.
  const bounds = getNodesBounds(nodes);
  const w = Math.max(1, Math.ceil(bounds.width + pad * 2));
  const h = Math.max(1, Math.ceil(bounds.height + pad * 2));
  const dataUrl = await toPng(viewportEl, {
    backgroundColor: "#ffffff",
    width: w,
    height: h,
    pixelRatio,
    cacheBust: true,
    style: {
      width: `${w}px`,
      height: `${h}px`,
      transform: `translate(${pad - bounds.x}px, ${pad - bounds.y}px) scale(1)`,
    },
  });
  const pdf = new jsPDF({
    orientation: w >= h ? "landscape" : "portrait",
    unit: "px",
    format: [w, h],
    compress: true,
  });
  pdf.addImage(dataUrl, "PNG", 0, 0, w, h);

  // 3) Write to the chosen location, or fall back to a default download.
  if (fileHandle) {
    const writable = await fileHandle.createWritable();
    await writable.write(pdf.output("blob"));
    await writable.close();
  } else {
    pdf.save(filename);
  }
}
